import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Dialog, Stack, Typography } from "@mui/material";
import { UnstyledButton } from "Components/Shared/Buttons/UnstyledButton";
import { CloseIcon } from "Components/Shared/Icons";
import { useIsMobile } from "Hooks/useIsMobile";
import { VISUALLY_HIDDEN_STYLE } from "Components/Shared/visuallyHiddenStyle";
import { Resources, useResource } from "Translations/Resources";
import { type ReactNode, useId } from "react";

type Props = {
  /** Omit for dialogs that render their own centered heading in `children`;
      the header row then holds just the close button. */
  title?: string;
  children: ReactNode;
  onClose: () => void;
  isOpen: boolean;
  onBack?: () => void;
  /** A full-bleed bar below the content — outside the padding and the scrolling. */
  footer?: ReactNode;
  /** Names the dialog for assistive technology when `title` is omitted because the
      heading is rendered inside `children`. One of the two should always be set: MUI
      always points `aria-labelledby` at an id of its own making, so with neither the
      reference dangles and the dialog ends up with no accessible name at all. */
  accessibilityLabel?: string;
};

export const ModalDialog = ({
  title,
  onClose,
  children,
  isOpen,
  onBack,
  footer,
  accessibilityLabel,
}: Props) => {
  const isMobile = useIsMobile();
  const { t } = useResource();
  const titleId = useId();
  const label = title ?? accessibilityLabel;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{
        paper: {
          sx: {
            boxShadow: "none",
            borderRadius: "10px",
            width: "100%",
            mt: isMobile ? "-30px" : undefined, //30px is half of the mobile menu height
          },
        },
      }}
    >
      {/* Always space-between: with neither a back arrow nor a title the leading
          Stack collapses to nothing and the close button still sits at the end. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        px={5}
        pt={5}
      >
        <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
          {!!onBack && (
            <UnstyledButton
              onClick={onBack}
              aria-label={t(Resources.Common.Back)}
              sx={{ cursor: "pointer", mt: 1 }}
            >
              <ArrowBackIosNewRoundedIcon fontSize="small" />
            </UnstyledButton>
          )}
          {!!title && (
            <Typography id={titleId} variant="h6">
              {title}
            </Typography>
          )}
          {!title && !!label && (
            <Typography id={titleId} style={VISUALLY_HIDDEN_STYLE}>
              {label}
            </Typography>
          )}
        </Stack>
        <UnstyledButton onClick={onClose} sx={{ cursor: "pointer", mt: 1 }}>
          <CloseIcon width={18} height={18} />
        </UnstyledButton>
      </Stack>
      {/* Content is rendered directly in the paper (no DialogContent), so this
          Stack owns the scrolling — keeping the title row and close button
          visible when content exceeds the dialog's maxHeight. */}
      <Stack
        px={5}
        pb={footer ? 4 : 5}
        pt={4}
        gap={4}
        overflow="auto"
        minHeight={0}
      >
        {children}
      </Stack>
      {!!footer && (
        <Stack
          px={5}
          py={3}
          borderTop={1}
          borderColor="divider"
          bgcolor="grey.50"
        >
          {footer}
        </Stack>
      )}
    </Dialog>
  );
};
